import { useState, useEffect, useContext } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import API from "../../API";
import Brand from "../widgets/brands/Brand";
import BreadCrumbs from "../widgets/BreadCrumbs";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import ItemImage from "../widgets/shop/ItemImage";
import React from "react";
import Button from "@mui/material/Button";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";
import CartContext from "../../contexts/CartContext";
import CountInput from "../widgets/inputs/CountInput";
import ItemNotes from "../widgets/details/ItemNotes";
import PriceSummary from "../widgets/shop/PriceSummary";
import IfFalse from "../conditions/IfFalse";
import IfTrue from "../conditions/IfTrue";
import VariantSelector from "../widgets/details/VariantSelector";
import Price from "../widgets/shop/Price";
import { Alert, IconButton } from "@mui/material";
import DeliveryNotes from "../widgets/details/DeliveryNotes";
import Snackbar from "@mui/material/Snackbar";
import ShoppingCartCheckoutIcon from "@mui/icons-material/ShoppingCartCheckout";
import ConfigContext from "../../contexts/ConfigContext";
import ReactMarkdown from "react-markdown";

// Show a "low stock" warning when remaining inventory is at or below this
// number. Set to 0 to effectively disable the warning (stock > 0 will never
// be <= 0). Raise this if/when we want to nudge customers on scarce items.
const LOW_STOCK_THRESHOLD = 0;

const curatorIconUrl = `${process.env.PUBLIC_URL || ''}/images/curator-icon.svg`;
const butlerIconUrl = `${process.env.PUBLIC_URL || ''}/images/butler-icon.svg`;

const CuratorIcon = ({ size = 28 }: { size?: number }) => (
  <img
    src={curatorIconUrl}
    alt=""
    aria-hidden="true"
    style={{ width: size, height: size, display: 'block' }}
  />
);

const ButlerIcon = ({ size = 28 }: { size?: number }) => (
  <img
    src={butlerIconUrl}
    alt=""
    aria-hidden="true"
    style={{ width: size, height: size, display: 'block' }}
  />
);

const ItemDetailsPage = () => {
  const { config } = useContext(ConfigContext);
  // `store` comes from the URL pattern (`/r/:store/...`). We pass it
  // explicitly to API calls because the user can land here from Home
  // without ever activating the tenant (the background-location modal
  // pattern lets the dialog open over Home, so the API singleton's
  // active store may not match the item's tenant).
  let { itemId, store } = useParams<{ itemId: string; store: string }>();
  const [item, setItem] = useState<IItem>({
    id: "",
    name: "",
    description: "",
    imageId: "",
    categoryId: "",
    supplierUrls: [],
    homeUrls: [],
    externalId: {},
    inventory: {},
  });

  const [reloadPage, setReloadPage] = React.useState<boolean>(true);
  const [selectedVariation, setSelectedVariation] = React.useState<IItem>(item);
  const [variants, setVariants] = useState<IItem[]>([selectedVariation]);
  const [closing, setClosing] = useState<boolean>(false);
  const [prevItemId, setPrevItemId] = useState<string | undefined>(itemId);
  const [descriptions, setDescriptions] = useState<Record<string, string>>({});
  const [selectedPersonality, setSelectedPersonality] = useState<string>('default');
  const [personalityDisclaimers, setPersonalityDisclaimers] = useState<Record<string, string | null>>({});
  // Sirv path root for THIS item's tenant. We can't rely on
  // ConfigContext (which holds whichever tenant TenantRoute last
  // activated) because the dialog can open over Home — where no
  // tenant has been activated for the URL's store. Fetched on mount
  // from the URL-tenant's client config; falls back to context's
  // `general.root_category` if the fetch fails.
  const [tenantImageRoot, setTenantImageRoot] = useState<string | undefined>(undefined);

  if (prevItemId !== itemId) {
    setPrevItemId(itemId);
    setClosing(false);
  }

  // Get the cart from the context
  const { cart, setCartState } = useContext(CartContext);

  const [itemCount, setItemCount] = React.useState<number>(1);

  const history = useHistory();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [checkoutEnabled, setCheckoutEnabled] = React.useState(false);

  useEffect(() => {
    if (itemId) {
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "auto";
      };
    }
  }, [itemId]);

  useEffect(() => {
    setItemCount(1);
  }, [selectedVariation.id]);

  useEffect(() => {
    let cancelled = false;
    if (!selectedVariation.id) {
      setDescriptions({});
      return;
    }
    API.getItemDescriptions(selectedVariation.id, undefined, store)
      .then((map) => {
        if (!cancelled) {
          setDescriptions(map || {});
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDescriptions({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, [selectedVariation.id]);

  useEffect(() => {
    if (selectedPersonality !== 'default' && !descriptions[selectedPersonality]) {
      setSelectedPersonality('default');
    }
  }, [descriptions, selectedPersonality]);

  useEffect(() => {
    if (!store) return;
    let cancelled = false;
    (async () => {
      try {
        const cfg = await API.getClientConfig('client', undefined, store);
        if (cancelled) return;
        const root = (cfg as any)?.general?.root_category;
        if (typeof root === 'string' && root) {
          setTenantImageRoot(root);
        }
      } catch {
        // Fall through to ConfigContext on failure — the active
        // tenant's root may still happen to match if the user came
        // from the same shop.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [store]);

  useEffect(() => {
    let cancelled = false;
    API.getAiPersonalities()
      .then((list) => {
        if (cancelled) return;
        const map: Record<string, string | null> = {};
        for (const p of list) {
          if (p && p.id) {
            map[p.id] = p.disclaimer ?? null;
          }
        }
        setPersonalityDisclaimers(map);
      })
      .catch(() => {
        if (!cancelled) {
          setPersonalityDisclaimers({});
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * From materialUI "Integration with 3rd party input libraries":
   *   https://material-ui.com/components/text-fields/#integration-with-3rd-party-input-libraries
   */
  const handleChange = (value: number) => {
    setItemCount(value);
  };

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const item = await API.getItemById(itemId, undefined, store);
        setItem(item);
        setSelectedVariation(item);

        if (item.group?.enabled && !item.group.variantName) {
          // Download the information about the variants as well.
          setVariants(await API.getVariantsById(itemId, undefined, store));
        } else {
          // This is its own variant
          setVariants([item]);
        }
      } catch (err: any) {
        setItem({
          id: "ERROR",
          name: err.message,
          categoryId: "",
          supplierUrls: [],
          homeUrls: [],
          externalId: {},
          inventory: {},
        });
        console.error(err);
      }
    };
    if (itemId) {
      setItem({
        id: "",
        name: "",
        description: "",
        imageId: "",
        categoryId: "",
        supplierUrls: [],
        homeUrls: [],
        externalId: {},
        inventory: {},
      });
      fetchItem();
    }
    setCheckoutEnabled(false);
  }, [itemId, reloadPage]);

  if (!itemId) {
    return <></>;
  }

  const selectedImageId = selectedVariation.imageId
    ? selectedVariation.imageId
    : item.imageId;

  // Hoisted above the early return so the loading/error placeholder
  // can wire its close button to the same handler the loaded dialog
  // uses. Only depends on state + hooks declared at the top of the
  // component, so it's safe to declare here.
  const goBack = () => {
    if (closing) return;
    setClosing(true);
    setTimeout(() => {
      if (location.state && (location.state as any).from) {
        history.goBack();
      } else {
        history.push("/");
      }
    }, 400);
  };

  // Loading / error placeholders use the same outer shape (overlay +
  // #id-main + header bar) as the loaded dialog below, so React's
  // reconciler doesn't unmount and remount `#id-main` when the item
  // finishes fetching — otherwise the CSS open-animation on the
  // dialog would play twice (once for the loading-state mount, once
  // for the loaded-state mount). Only the contents of `.dialog-body`
  // swap.
  if (!item.id || "ERROR" === item.id) {
    const placeholder =
      "ERROR" === item.id
        ? `Error loading item: ${item.name}`
        : `Loading item: ${itemId}`;
    return (
      <>
        {/* Empty Snackbar slot — reserves the same position 0 in the
            Fragment as the loaded-state render, so React reconciles
            into a stable tree instead of swapping the root element. */}
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={false}
        >
          <span />
        </Snackbar>
        <div className={`overlay${closing ? ' closing' : ''}`} onClick={goBack}></div>
        <div id="id-main" className={closing ? 'closing' : ''}>
          <div className="header-bar">
            <div className="spacer"></div>
            <IconButton
              className="close-icon"
              aria-label="back"
              onClick={goBack}
              size="large"
            >
              <CloseIcon />
            </IconButton>
          </div>
          <div className="dialog-body">{placeholder}</div>
        </div>
      </>
    );
  }

  const addToCart = () => {
    const useItem = selectedVariation ? selectedVariation : item;
    const count = cart.items[useItem.id] ? cart.items[useItem.id].count : 0;
    let newCount = +count + +itemCount;
    if (newCount > stockCounter) {
      newCount = stockCounter;
    }
    // Snapshot the tenant + image root so a mixed-tenant cart still renders
    // each item correctly even when viewed from the other tab.
    const newCart = {
      ...cart,
      items: {
        ...cart.items,
        [useItem.id]: {
          item: useItem,
          count: newCount,
          tenant: API.getStore() ?? undefined,
          imageRoot: config?.general?.root_category,
        },
      },
    };
    setCartState(newCart);
    setOpen(true);
    setCheckoutEnabled(true);
  };

  const handleClose = (reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const handleCheckout = () => {
    history.push("/order/checkout");
  };

  const stockCounter = Object.keys(selectedVariation.inventory).reduce(
    (sum, key) => sum + selectedVariation.inventory[key].count,
    0,
  );
  const inCartCount = cart.items[selectedVariation.id]
    ? cart.items[selectedVariation.id].count
    : 0;
  const remainingCapacity = Math.max(stockCounter - inCartCount, 0);
  const outOfStock = stockCounter === 0;
  const atCartMax = !outOfStock && inCartCount >= stockCounter;
  const lowStock = stockCounter > 0 && stockCounter <= LOW_STOCK_THRESHOLD;
  const disabled = config.general.site_disabled;

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={open}
        autoHideDuration={6000}
        onClose={(event?: any, reason?: string) => {
          handleClose(reason);
        }}
      >
        <Alert
          onClose={(event?: any, reason?: string) => {
            handleClose(reason);
          }}
          severity="success"
        >
          Added {item.name} to cart.
        </Alert>
      </Snackbar>
      <div className={`overlay${closing ? ' closing' : ''}`} onClick={goBack}></div>
      <div id="id-main" className={closing ? 'closing' : ''}>
        <div className="header-bar">
          <div className="spacer"></div>
          <IconButton
            className="close-icon"
            aria-label="back"
            onClick={goBack}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </div>
        <div className="dialog-body">
          {/* "You are here" path. Category links close the dialog by
              navigating the URL out of /item/:itemId; the parent-item
              link (variant case) swaps in the parent's dialog. */}
          <BreadCrumbs item={item} variant={selectedVariation} />
          <div className="hlayout">
            <Brand name={item.brand ? item.brand : ""} />
          </div>
          <div className="id-title">{item.name}</div>
          <div className="id-content">
            <Card className="id-image">
              <CardContent>
                <ItemImage
                  notLazy={true}
                  width={400}
                  id={selectedImageId}
                  title={item.name}
                  timestamp={item.updatedAt?.toString()}
                  imageRoot={tenantImageRoot}
                />
              </CardContent>
            </Card>
            <div className="id-text-content">
              <div>
                {(() => {
                  const defaultText = selectedVariation.description
                    ? selectedVariation.description
                    : item.description
                      ? item.description
                      : "";
                  const curatorText = (descriptions.curator ?? "").trim();
                  const butlerText = (descriptions.butler ?? "").trim();
                  const hasCurator = !!curatorText;
                  const hasButler = !!butlerText;

                  const selectedHasText =
                    selectedPersonality !== 'default' && !!(descriptions[selectedPersonality] ?? '').trim();
                  const personality = selectedHasText ? selectedPersonality : 'default';
                  const displayText =
                    personality === 'default'
                      ? defaultText
                      : (descriptions[personality] ?? '');

                  const inactiveStyle: React.CSSProperties = { color: '#999', opacity: 0.45, cursor: 'pointer' };
                  const activeStyle: React.CSSProperties = { color: '#222', opacity: 1, cursor: 'pointer' };

                  return (
                    <>
                      <div
                        className="id-description-personality"
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          gap: '0.5em',
                          alignItems: 'center',
                          marginBottom: '0.25em',
                        }}
                      >
                        <IconButton
                          aria-label="Standard description"
                          title="Standard description"
                          size="small"
                          onClick={() => setSelectedPersonality('default')}
                          style={personality === 'default' ? activeStyle : inactiveStyle}
                        >
                          <StorefrontIcon />
                        </IconButton>
                        <IfTrue condition={hasCurator}>
                          <IconButton
                            aria-label="Curator description"
                            title="Curator description"
                            size="small"
                            onClick={() => setSelectedPersonality('curator')}
                            style={personality === 'curator' ? activeStyle : inactiveStyle}
                          >
                            <CuratorIcon />
                          </IconButton>
                        </IfTrue>
                        <IfTrue condition={hasButler}>
                          <IconButton
                            aria-label="Butler description"
                            title="Butler description"
                            size="small"
                            onClick={() => setSelectedPersonality('butler')}
                            style={personality === 'butler' ? activeStyle : inactiveStyle}
                          >
                            <ButlerIcon />
                          </IconButton>
                        </IfTrue>
                      </div>
                      {displayText ? (
                        <ReactMarkdown>{displayText}</ReactMarkdown>
                      ) : (
                        <div className="id-description-placeholder">
                          No description available — call <b>(902) 530-0420</b> for
                          more details about this product.
                        </div>
                      )}
                      {personality !== 'default' && displayText && personalityDisclaimers[personality] && (
                        <div
                          className="id-description-personality-disclaimer"
                          style={{
                            fontStyle: 'italic',
                            fontSize: '0.8em',
                            opacity: 0.7,
                            marginTop: '0.75em',
                            lineHeight: 1.4,
                          }}
                        >
                          <ReactMarkdown>{personalityDisclaimers[personality] as string}</ReactMarkdown>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
              <div>
                <div>
                  <IfTrue
                    condition={
                      item.group?.enabled && item.id === selectedVariation.id
                    }
                  >
                    <PriceSummary variations={variants} />
                  </IfTrue>
                  <IfFalse
                    condition={
                      item.group?.enabled && item.id === selectedVariation.id
                    }
                  >
                    <Price variation={selectedVariation} />

                    <DeliveryNotes
                      stock={stockCounter}
                      specialDelivery={!!item.special}
                    />
                  </IfFalse>
                </div>
              </div>
              <ItemNotes
                notes={
                  selectedVariation.notes
                    ? selectedVariation.notes
                    : item.notes
                      ? item.notes
                      : ""
                }
              />
              <div>
                <input
                  type="hidden"
                  id="purchase-variation"
                  name="variation"
                  value={selectedVariation.id}
                ></input>
              </div>
              <div>
                <IfTrue
                  condition={item.group?.enabled && !item.group.variantName}
                >
                  <VariantSelector
                    item={item}
                    variants={variants}
                    setSelectedVariation={setSelectedVariation}
                  />
                </IfTrue>
              </div>
              <IfTrue condition={item.discontinued}>
                <Alert severity="error">
                  <div className="discontinued-text">
                    This item has been discontinued: {stockCounter} left in
                    stock
                  </div>
                </Alert>
              </IfTrue>

              <IfTrue condition={disabled}>
                <Alert severity="error">
                  Ordering from the site is temporarily disabled while we
                  perform yearly inventory and restock. Please call{" "}
                  <b>(902) 530-0420</b> to order by phone with one of our super
                  helpful staff. Sorry for the inconvenience.
                </Alert>
              </IfTrue>

              <div>
                <div>
                  <IfFalse
                    condition={
                      selectedVariation.group?.enabled &&
                      !selectedVariation.group.variantName
                    }
                  >
                    <IfTrue condition={outOfStock}>
                      <Alert severity="error">Out of stock</Alert>
                    </IfTrue>
                    <IfFalse condition={outOfStock}>
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddShoppingCartIcon />}
                        disabled={
                          !(+itemCount > 0) ||
                          !selectedVariation.id ||
                          disabled ||
                          atCartMax
                        }
                        onClick={addToCart}
                      >
                        Add to Cart
                      </Button>
                      <br />
                      <CountInput
                        handleCommit={handleChange}
                        initial={+itemCount}
                        max={remainingCapacity}
                      />
                      <IfTrue condition={inCartCount > 0}>
                        <div className="stock-label">
                          {inCartCount} in cart
                        </div>
                      </IfTrue>
                      <div className="stock-label">
                        {stockCounter} in stock
                      </div>
                      <IfTrue condition={lowStock}>
                        <Alert severity="warning">
                          Only {stockCounter} left!
                        </Alert>
                      </IfTrue>
                      <IfTrue condition={atCartMax}>
                        <Alert severity="info">Max in cart</Alert>
                      </IfTrue>
                    </IfFalse>
                  </IfFalse>
                </div>
                <div className="flex-div flex-spacing">
                  <IfTrue condition={checkoutEnabled}>
                    {/* <Button className='drawer-button' variant="outlined" color="secondary" startIcon={<ShoppingCartIcon />} onClick={handleModifyCart}>
                        View Cart
                    </Button> */}
                    <Button
                      className="drawer-button"
                      variant="outlined"
                      color="primary"
                      startIcon={<ShoppingCartCheckoutIcon />}
                      onClick={handleCheckout}
                    >
                      Checkout
                    </Button>
                  </IfTrue>
                </div>
              </div>
            </div>
          </div>
          {/* <pre>
            {JSON.stringify(item, null, 1)}
          </pre> */}
        </div>
      </div>
    </>
  );
};

export default ItemDetailsPage;
